import { portalText, usePortalTranslation } from "../../../i18n/portal.js";
/**
 * `Organization` → Finance → Funding — the payment status and recovery block (plan-014 D3b; design §27.15.1 "recovery
 * status", §27.8.16; FR12-BIL-08). Drawn to the prototype's Organization block renderer (`design/WeTalk.dc.html`,
 * `data-screen-label="Organization"`, the Finance pages): a block head (title, one-line dek) over a stats row
 * ("Charged", "Credit", "VAT") and a notice that says what the payment is waiting on, in the prototype's own words —
 * "Waiting on the bank" (an unknown outcome being looked up) and "Paid, not applied" (resumed exactly once) are the
 * operator Finance page's labels, carried to the person whose money it is.
 *
 * WHAT IT NEVER DOES: report an outcome. The page the person comes back to from the processor reads the status
 * (`readOperationPayment`) and re-reads it while the operation is open; the outcome arrives from the processor through
 * WeTalk's payment worker. "Pay now" opens the processor's page for an automatic top-up waiting for a click
 * (collection mode `hosted_notification`) or re-opens the page an operation already waits on — never a second payment.
 *
 * D4 owns the Funding page; this block is mounted by it for `?operation=<id>` (the return URL's search).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { money, type CurrencyCode } from "@wetalk/domain";
import { Button, Icon, Tag } from "@wetalk/ui";
import type { ReactNode } from "react";

import { apiProblemText } from "../../../api/api-error.js";
import { useGateway } from "../../../api/provider.js";
import type { OperationPaymentView, PaymentStatusWordView } from "../../../api/slice/billing-operation.js";
import { formatMoney } from "../../../money.js";
import { GLYPH } from "../../../shell/glyph.js";

import "./payment-status.css";

/** How often an open operation's status is read again (a read, never a report). */
export const PAYMENT_STATUS_REFRESH_MS = 5_000;

export function operationPaymentQueryKey(operationId: string): readonly ["operation-payment", string] {
  return ["operation-payment", operationId];
}

/** The statuses after which nothing more will happen to the operation. */
const SETTLED: ReadonlySet<PaymentStatusWordView> = new Set<PaymentStatusWordView>(["completed", "failed", "cancelled"]);

type Tone = "positive" | "caution" | "critical" | "muted";

/** The status line, in the prototype's words. */
export const PAYMENT_STATUS_COPY: Readonly<Record<PaymentStatusWordView, { readonly label: string; readonly line: string; readonly tone: Tone }>> = {
  awaiting_operator_approval: {
    get label() { return portalText("finance", "m0589"); },
    get line() { return portalText("finance", "m1148"); },
    tone: "caution",
  },
  pay_now: {
    get label() { return portalText("finance", "m0590"); },
    get line() { return portalText("finance", "m1149"); },
    tone: "caution",
  },
  draft: { get label() { return portalText("finance", "m0591"); }, get line() { return portalText("finance", "m1150"); }, tone: "muted" },
  payment_starting: { get label() { return portalText("finance", "m0592"); }, get line() { return portalText("finance", "m1151"); }, tone: "muted" },
  payment_page_open: {
    get label() { return portalText("finance", "m0593"); },
    get line() { return portalText("finance", "m1152"); },
    tone: "caution",
  },
  awaiting_payment: {
    get label() { return portalText("finance", "m0593"); },
    get line() { return portalText("finance", "m1153"); },
    tone: "caution",
  },
  waiting_on_bank: {
    get label() { return portalText("finance", "m0594"); },
    get line() { return portalText("finance", "m1154"); },
    tone: "caution",
  },
  paid_not_applied: {
    get label() { return portalText("finance", "m0595"); },
    get line() { return portalText("finance", "m1155"); },
    tone: "positive",
  },
  applying: { get label() { return portalText("finance", "m0596"); }, get line() { return portalText("finance", "m1156"); }, tone: "positive" },
  completed: { get label() { return portalText("finance", "m0597"); }, get line() { return portalText("finance", "m1157"); }, tone: "positive" },
  failed: { get label() { return portalText("finance", "m0598"); }, get line() { return portalText("finance", "m1158"); }, tone: "critical" },
  cancelled: { get label() { return portalText("finance", "m0599"); }, get line() { return portalText("finance", "m1159"); }, tone: "muted" },
};

/**
 * What the payment buys (plan-014 review I-22): credit (a top-up, an automatic top-up, a refund of credit), a plan or
 * add-on, or a postpaid statement — so a plan buyer is never told "Your credit is being added".
 */
export type PaymentPurpose = "credit" | "plan" | "statement";

const PLAN_KIND: ReadonlySet<string> = new Set(["subscription_purchase", "subscription_change", "subscription_reactivation", "add_on_change"]);

export function paymentPurposeOf(kind: string): PaymentPurpose {
  if (PLAN_KIND.has(kind)) return "plan";
  if (kind === "statement_payment") return "statement";
  return "credit";
}

/** The status lines that name what was bought, per purpose; every other status reads the same for all. */
const PURPOSE_COPY: Readonly<Record<Exclude<PaymentPurpose, "credit">, Partial<Record<PaymentStatusWordView, { readonly line: string }>>>> = {
  plan: {
    paid_not_applied: { get line() { return portalText("finance", "m1160"); } },
    applying: { get line() { return portalText("finance", "m1161"); } },
    completed: { get line() { return portalText("finance", "m1162"); } },
  },
  statement: {
    paid_not_applied: { get line() { return portalText("finance", "m1163"); } },
    applying: { get line() { return portalText("finance", "m1164"); } },
    completed: { get line() { return portalText("finance", "m1165"); } },
  },
};

/** The status line for this payment: the shared words, with what was bought named where it differs. */
export function paymentStatusCopyOf(status: PaymentStatusWordView, kind: string): { readonly label: string; readonly line: string; readonly tone: Tone } {
  const shared = PAYMENT_STATUS_COPY[status];
  const purpose = paymentPurposeOf(kind);
  if (purpose === "credit") return shared;
  const named = PURPOSE_COPY[purpose][status];
  return named === undefined ? shared : { ...shared, line: named.line };
}

/** What the operation is, in a few words. */
export function operationKindTextOf(kind: string): string {
  switch (kind) {
    case "top_up":
      return portalText("finance", "m0143");
    case "subscription_purchase":
      return portalText("finance", "m0600");
    case "subscription_change":
      return portalText("finance", "m0601");
    case "subscription_reactivation":
      return portalText("finance", "m0602");
    case "add_on_change":
      return portalText("finance", "m0603");
    case "statement_payment":
      return portalText("finance", "m0604");
    case "auto_reload":
      return portalText("finance", "m0605");
    case "refund":
      return portalText("finance", "m0606");
    default:
      return kind.replaceAll("_", " ").replace(/^./, (first) => first.toUpperCase());
  }
}

function amountText(minor: number | null, currency: string): string {
  return minor === null ? "—" : formatMoney(money(minor, currency as CurrencyCode));
}

export interface PaymentStatusBlockProps {
  readonly operationId: string;
}

export function PaymentStatusBlock(props: PaymentStatusBlockProps): ReactNode {
  usePortalTranslation("finance");
  const gateway = useGateway();
  const queryClient = useQueryClient();
  const key = operationPaymentQueryKey(props.operationId);
  const read = useQuery({
    queryKey: key,
    queryFn: () => gateway.readOperationPayment(props.operationId),
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === undefined || SETTLED.has(status) ? false : PAYMENT_STATUS_REFRESH_MS;
    },
  });
  const pay = useMutation({
    mutationFn: () => gateway.payOperation(props.operationId),
    onSuccess: (view) => queryClient.setQueryData(key, view),
  });
  const cancel = useMutation({
    mutationFn: () => gateway.cancelOperation(props.operationId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: key }),
  });

  if (read.isPending) {
    return (
      <section className="ps-block" aria-busy="true">
        <p className="ps-dek">{portalText("finance", "m0607")}</p>
      </section>
    );
  }
  if (read.isError) {
    return (
      <section className="ps-block">
        <div className="ps-notice ps-notice-critical" role="alert">
          <Icon glyph={GLYPH.warningCircle} size="xl" />
          <p className="ps-notice-text">{apiProblemText(read.error, portalText("finance", "m0608"))}</p>
          <Button variant="secondary" onClick={() => void read.refetch()}>{portalText("finance", "m0382")}</Button>
        </div>
      </section>
    );
  }

  const view: OperationPaymentView = read.data;
  const copy = paymentStatusCopyOf(view.status, view.kind);
  const purpose = paymentPurposeOf(view.kind);
  const openUrl = view.nextActionUrl ?? view.payUrl;
  const glyph = copy.tone === "positive" ? GLYPH.checkCircle : copy.tone === "critical" ? GLYPH.xCircle : GLYPH.clockCounterClockwise;
  return (
    <section className="ps-block" aria-labelledby="ps-title">
      <div className="ps-block-head">
        <div className="ps-block-heading">
          <h2 id="ps-title" className="ps-block-title">
            {operationKindTextOf(view.kind)}
          </h2>
          <p className="ps-dek">
            {purpose === "credit" ? portalText("finance", "m0609") : portalText("finance", "m0610")}
          </p>
        </div>
        <Tag status={copy.tone}>{copy.label}</Tag>
      </div>

      <dl className="ps-stats">
        <div className="ps-stat">
          <dt className="ps-stat-label">
            {view.kind === "refund" ? portalText("finance", "m0611") : purpose === "plan" ? portalText("finance", "m0612") : purpose === "statement" ? portalText("finance", "m0448") : portalText("finance", "m0148")}
          </dt>
          <dd className="ps-stat-value">{amountText(view.netMinor, view.currency)}</dd>
        </div>
        <div className="ps-stat">
          <dt className="ps-stat-label">{portalText("finance", "m0613")}</dt>
          <dd className="ps-stat-value">{amountText(view.taxMinor, view.currency)}</dd>
        </div>
        <div className="ps-stat">
          <dt className="ps-stat-label">{portalText("finance", "m0614")}</dt>
          <dd className="ps-stat-value">{amountText(view.amountDueMinor, view.currency)}</dd>
        </div>
      </dl>

      <div className={`ps-notice ps-notice-${copy.tone}`} role="status">
        <Icon glyph={glyph} size="xl" />
        <p className="ps-notice-text">{copy.line}</p>
      </div>

      {(pay.isError || cancel.isError) && (
        <p className="ps-error" role="alert">
          {apiProblemText(pay.isError ? pay.error : cancel.error, portalText("finance", "m0615"))}
        </p>
      )}

      <div className="ps-actions">
        {view.status === "pay_now" && (
          <Button variant="primary" disabled={pay.isPending} onClick={() => pay.mutate()}>{portalText("finance", "m0616")}</Button>
        )}
        {openUrl !== null && view.status !== "pay_now" && (
          <a className="btn btn-primary" href={openUrl} rel="noopener noreferrer">{portalText("finance", "m0617")}</a>
        )}
        {view.cancellable && view.status !== "awaiting_operator_approval" && (
          <Button variant="ghost" disabled={cancel.isPending} onClick={() => cancel.mutate()}>{portalText("finance", "m0418")}</Button>
        )}
      </div>
    </section>
  );
}
